<script>
  import L from 'leaflet';
  import 'leaflet/dist/leaflet.css';
  import { onMount, onDestroy } from 'svelte';
  import UA from 'ua-parser-js';
  const ua = new UA();
  let isMobile = ua.getResult().device['type'] == 'mobile';

  import MapScripting from './MapScripting.svelte';
  import {
    getImageMapMaxZoom,
    getImageMapMinZoomAfterFit,
    IMAGE_MAP_MIN_ZOOM_FOR_CONSTRUCTOR,
    IMAGE_MAP_ZOOM_DELTA,
    IMAGE_MAP_ZOOM_SNAP,
  } from './mapImageZoom.js';
  import { syncImageMapMaxBoundsForZoom } from './mapImageCoords.js';

  export let mapComponent;
  export let tour;
  export let expanded;
  /** Ignored for image maps — Mapbox zoom levels are not valid for Leaflet CRS.Simple. */
  export let zoom;
  export let mobileLocationsOpen = false;
  export let onCloseMobileLocations = () => {};
  export let focusLocationsFilterOnOpen = false;
  export let onFocusLocationsFilterDone = () => {};
  export let showLocationsMenu = true;

  let mapContainer;
  let mapInstance = null;
  /** Tracks last `expanded` so we can refit when opening full map after starting on a scene (minimap). */
  let previousExpanded = undefined;

  $: if (expanded !== null && mapInstance) {
    mapInstance.invalidateSize();
    // Minimap mounts first on #/stop/…; fit() ran at tiny size. Refit when expanding so zoom matches #/map.
    if (expanded === true && previousExpanded === false) {
      const m = mapInstance;
      if (typeof m.fitImageMapToView === 'function') {
        requestAnimationFrame(() => m.fitImageMapToView());
        setTimeout(() => m.fitImageMapToView(), 50);
        setTimeout(() => m.fitImageMapToView(), 350);
      }
    }
    previousExpanded = expanded;
  }

  onMount(() => {
    const url = tour?.mapbox?.mapImageUrl;
    if (!mapContainer || !url) {
      console.error('Image map: container or mapImageUrl missing');
      return;
    }

    const w = tour?.mapImage?.width;
    const h = tour?.mapImage?.height;
    const maxZoom =
      w > 0 || h > 0 ? getImageMapMaxZoom(w, h) : 18;

    const map = L.map(mapContainer, {
      crs: L.CRS.Simple,
      minZoom: IMAGE_MAP_MIN_ZOOM_FOR_CONSTRUCTOR,
      maxZoom,
      zoom: 0,
      zoomSnap: IMAGE_MAP_ZOOM_SNAP,
      zoomDelta: IMAGE_MAP_ZOOM_DELTA,
      attributionControl: false,
      zoomControl: false,
    });
    map._isLeafletImageMap = true;

    let aspectWOverH = 1;
    if (w > 0 && h > 0) {
      aspectWOverH = w / h;
    }
    let bounds = L.latLngBounds(L.latLng(0, 0), L.latLng(1 / aspectWOverH, 1));
    map._imageAspectWOverH = aspectWOverH;
    const overlay = L.imageOverlay(url, bounds).addTo(map);

    const syncBoundsFromZoom = () => {
      syncImageMapMaxBoundsForZoom(L, map, aspectWOverH);
    };
    map.on('zoomend', syncBoundsFromZoom);

    const fit = () => {
      map.invalidateSize();
      map.fitBounds(bounds, { animate: false, padding: [2, 2] });
      map.setMinZoom(getImageMapMinZoomAfterFit(map.getZoom()));
      syncBoundsFromZoom();
    };
    map.fitImageMapToView = fit;
    overlay.on('load', () => {
      const el = overlay.getElement?.();
      if (!(w > 0 || h > 0) && el?.naturalWidth > 0 && el?.naturalHeight > 0) {
        aspectWOverH = el.naturalWidth / el.naturalHeight;
        bounds = L.latLngBounds(L.latLng(0, 0), L.latLng(1 / aspectWOverH, 1));
        overlay.setBounds(bounds);
        map._imageAspectWOverH = aspectWOverH;
        map.setMaxZoom(getImageMapMaxZoom(el.naturalWidth, el.naturalHeight));
      }
      fit();
    });
    const img = overlay.getElement?.();
    if (img && img.complete && img.naturalWidth) fit();
    requestAnimationFrame(fit);
    setTimeout(fit, 50);
    setTimeout(fit, 200);

    const ro = new ResizeObserver(() => {
      if (!map) return;
      map.invalidateSize();
    });
    ro.observe(mapContainer);

    mapInstance = map;
    mapComponent = map;

    return () => {
      map.off('zoomend', syncBoundsFromZoom);
      ro.disconnect();
    };
  });

  onDestroy(() => {
    if (mapInstance) {
      mapInstance.remove();
      mapInstance = null;
    }
  });
</script>

<div
  class="map map-image"
  bind:this={mapContainer}
  role="button"
  tabindex="0"
  on:click={() => {
    if (!expanded && window.CC?.switchView) {
      window.CC.switchView();
    }
  }}
  on:keydown={(e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !expanded && window.CC?.switchView) {
      e.preventDefault();
      window.CC.switchView();
    }
  }}
>
  {#if mapInstance}
    <MapScripting
      {tour}
      {expanded}
      map={mapInstance}
      showLocationsMenu={showLocationsMenu}
      {mobileLocationsOpen}
      {onCloseMobileLocations}
      focusLocationsFilterOnOpen={focusLocationsFilterOnOpen}
      onFocusLocationsFilterDone={onFocusLocationsFilterDone}
    />
  {/if}
</div>

<style>
  :global(.leaflet-container) {
    z-index: 1;
  }
</style>
